<template>
  <div
    :class="[
      'coupon-wrapper',
      {
        [`theme__${themeColor}`]: themeColor,
        coupon__disabled: isExpire,
        en: isEn,
      },
    ]"
    @click="couponClick"
  >
    <!-- 左上角 tag -->
    <div
      :class="['coupon-tag', { [`coupon-tag__${couponInfo.couponTagColor}`]: couponInfo.couponTagColor }]"
      v-if="couponInfo.couponTagText"
    >
      {{ couponInfo.couponTagText }}
    </div>
    <div class="coupon">
      <div class="coupon-content">
        <div class="coupon-left">
          <!-- 普通券，带奖品logo -->
          <div class="prize-logo" v-if="isDefaultCoupon">
            <img :src="couponInfo.prizeLogo || couponInfo.spLogo" alt="prize logo(優惠券獎品logo)" />
          </div>
          <!-- 现金券，带金额 -->
          <div class="coupon-amount" v-if="isCashCoupon">
            <Amount
            :currencyType="currencyType"
            :prizeAmountYuan="couponInfo.prizeAmountYuan"
            ></Amount>
            <span class="total-amount" v-if="couponInfo.pkgCount">
              {{ pkgCount }}
            </span>
          </div>
          <!-- 折扣券，带折扣 -->
          <div class="coupon-discount" v-if="isDiscountCoupon">
            <div class="coupon-discount-top">
              <Amount
                class="discount"
                :currencyType="currencyType"
                :prizeAmountPenny="couponInfo.prizeDiscount"
                :isDiscount="isDiscountCoupon"
                :zoomRatio="0.78"
              >
              </Amount>
            </div>
            <span class="total-amount" v-if="couponInfo.pkgCount">
              {{ pkgCount }}
            </span>
          </div>
          <!-- 处理半圆 -->
          <i class="semicircle"></i>
          <!-- 左侧扇形背景 -->
          <div class="coupon-left-bg"></div>
        </div>
        <div class="coupon-middle">
          <div
            class="coupon-sp-info"
            role="text"
            :aria-label="parseAriaLabel(couponInfo.spDesc)"
            v-if="couponInfo.spDesc"
          >
            <!-- 不展示商户 logo -->
            <div>{{ couponInfo.spDesc }}</div>
          </div>
          <!-- 如果没有券描述，则不限制券标题的行数 -->
          <div
            :class="['coupon-title', { 'no-limit-line': !couponInfo.prizeDesc }]"
            :aria-label="parseAriaLabel(couponInfo.prizeName)"
            role="text"
          >
            <!-- 金额 -->
            <template v-if="isDefaultCoupon">
              <!-- 折扣数值 -->
              <span class="coupon-title-discount" v-if="couponInfo.prizeDiscount">
                {{ discountText }}{{ couponInfo.pkgCount ? `×${couponInfo.pkgCount}` : '' }}
              </span>
              <!-- 金额数值 -->
              <span class="coupon-title-amount" v-else-if="couponInfo.prizeAmountYuan">
                {{ currencyCode }}{{ couponInfo.prizeAmountYuan
                }}{{ couponInfo.pkgCount ? `×${couponInfo.pkgCount}` : '' }}
              </span>
            </template>
            {{ couponInfo.prizeName }}
          </div>
          <div class="coupon-desc">
            <span
              :class="['tips-icon-wrapper', { single: !couponInfo.prizeDesc }]"
              v-if="isShowManualTips || isShowChainTips || isShowBankTips"
            >
              <!-- 提示条类型1 （手动图标） -->
              <i class="tips-icon icon-manual" v-if="isShowManualTips"></i>
              <!-- 提示条类型2 （连锁图标） -->
              <i class="tips-icon icon-chain" v-else-if="isShowChainTips"></i>
              <!-- 提示条类型3 （银行图标） -->
              <i class="tips-icon icon-bank" v-else-if="isShowBankTips"></i>
              <!-- 如果没有券描述，则展示具体的券类型提示 -->
              <span class="tips-icon-text" v-if="!couponInfo.prizeDesc">{{ couponInfo.tipsText }}</span>
            </span>
            <span class="coupon-desc-content" role="text" :aria-label="parseAriaLabel(couponInfo.prizeDesc)">
              {{ couponInfo.prizeDesc }}
            </span>
          </div>
        </div>
        <div
          :class="['coupon-right', { 'stamp-width': isShowStamp, 'tips-width': isShowTips, 'has-btn': isShowButton }]"
        >
          <div class="coupon-stamp" v-if="isShowStamp">
            <div class="coupon-stamp-inner">
              <div class="coupon-stamp-txt" v-html="couponInfo.stampText"></div>
            </div>
          </div>
          <div
            v-if="isShowButton"
            v-stat.click="{
              tag: 'comp-coupon-exchange.item-exchange',
              ext: { fprizeAmountYuan: couponInfo.prizeAmountYuan },
            }"
            @click.stop="handleExchange"
            :class="[
              'button',
              { [`button__${couponInfo.buttonType}`]: couponInfo.buttonType },
              { [`${enButtionStyle}`]: enButtionStyle },
            ]"
            role="button"
          >
            <!-- 不需要锁icon -->
            {{ couponInfo.couponBtnText }}
            <slot name="coupon-btn-ui"></slot>
          </div>
          <!-- 库存提示 -->
          <span class="tips-text-right" v-if="isShowTips">
            {{ couponInfo.tipsTextRight }}
          </span>
        </div>
        <!-- 主題 logo -->
        <img class="coupon-theme-logo" v-if="couponInfo.themeLogo" :src="couponInfo.themeLogo" alt="" />
      </div>
    </div>
    <!-- 如果是连锁券 or 券包 or 需要展示阴影，则会展示阴影 -->
    <div class="coupon-chain" v-if="isShowShadow">
      <div class="coupon-shadow-wrap">
        <div class="coupon-shadow"></div>
      </div>
      <div class="coupon-shadow-wrap">
        <div class="coupon-shadow"></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import CouponMixinV2 from './mixin/mixin-v2.vue';
@Component
export default class Coupon extends mixins(CouponMixinV2) {
  /** 是否是券包 */
  get isPkgCoupon() {
    return !!this.couponInfo.pkgCount;
  }
  /** 是否展示阴影,如果是连锁券 or 券包 or 需要展示阴影，则会展示阴影 */
  get isShowShadow() {
    return this.isChain || this.isPkgCoupon || this.couponInfo.isShowShadow;
  }
  /** 覆盖 mixin --- 是否展示优惠券底部提示条 */
  get isShowTips() {
    if (this.couponInfo.isShowTips) {
      return true;
    }
    const result = this.isExpire ? false : !!this.couponInfo.tipsTextRight;
    return result;
  }
  parseAriaLabel(text: string) {
    return this.parseCurrencyAria(text, true, this.currencyType);
  }
}
</script>

<style lang="less" scoped>
@import './less/coupon-v2.less';
</style>
